import React, { useEffect, useReducer } from "react";
import { styled } from '@mui/material/styles';
import Stack from "@mui/material/Stack";
import {
  EDButton,
  EDButtonType,
  EDOperationPopup,
  EDPageLoader,
  EDTypography,
  error
} from "@trailblazer-game/ed-webkit";
import LeagueHeader from "../Components/League_Header";
import LeagueLeaderboard from "../Components/League_Leaderboard";
import { useRouter } from "next/router";
import { SessionContext, SessionContextInterface } from "../../../../hooks/SessionHook/SessionContext";
import { EPage } from "../../../../helpers/constants";
import { ILeagueRank } from "../../../../helpers/interfaces/league";
import { log, warn } from "../../../../helpers/utils/log";
import { ELeagueActions, leagueInitialState, leagueReducer } from "./League_State";
import { LogMessage } from "../../../LogMessage/LogMessage";
import League_Prizes from "../Components/League_Rewards";
import { ClientLeagueAPI } from '@trailblazer-game/leaderboards-sdk';
import { addMillisecondsToTimestamp } from "../../../../helpers/dates";
import League_DebugTools from "../Components/League_DebugTools";
import Timeout = NodeJS.Timeout;

const clientLeagueAPI = new ClientLeagueAPI();
const REFRESH_TIMEOUT = 30 * 1000;
const MAX_TIMEOUT = 86.400 * 1000; // 1 day

interface ILeagueProps {
  className?: string;
  seasonId?: string;
}

function League({ className, seasonId }: ILeagueProps) {
  const [ state, dispatch ] = useReducer(leagueReducer, leagueInitialState);
  const { session } = React.useContext(SessionContext) as SessionContextInterface;
  const router = useRouter();

  useEffect(() => {
    let dataInterval = setInterval(() => {
      loadLeaderboardConfig();
    }, REFRESH_TIMEOUT);
    loadLeaderboardConfig();
    return () => clearInterval(dataInterval);
  }, []);

  useEffect(() => {
    const { activeWeek } = state;
    const timeouts: Timeout[] = [];

    if (!activeWeek || !activeWeek.startTimestamp || !activeWeek.endTimestamp) {
      return;
    }

    const now = new Date().getTime();
    const endTimestamp = addMillisecondsToTimestamp(activeWeek.endTimestamp);
    const startTimestamp = addMillisecondsToTimestamp(activeWeek.startTimestamp);

    const isEndTimestampInRange = endTimestamp - now < MAX_TIMEOUT && now - endTimestamp < 0;
    if (isEndTimestampInRange) {
      timeouts.push(setTimeout(loadLeaderboardConfig, endTimestamp - now));
    }

    const isStartTimestampInRange = startTimestamp - now < MAX_TIMEOUT && now - startTimestamp < 0;
    if (isStartTimestampInRange) {
      timeouts.push(setTimeout(loadLeaderboardConfig, startTimestamp - now));
    }

    loadRankings();

    return () => {
      timeouts.map(timeout => clearTimeout(timeout))
    };
  }, [ state.activeWeek ]);

  const getSeasonKey = () => state.config && state.activeWeek ? `${state.config?.seasonId}_${state.activeWeek?.key}` : undefined;

  const loadRankings = async () => {
    dispatch({ type: ELeagueActions.LoadRankings });

    const res = await clientLeagueAPI.getRanking(getSeasonKey()); // {success: true, data: fakeRanks()}

    if (res.success) {
      const data = res.data;
      dispatch({ type: ELeagueActions.LoadRankingsSuccess, ranks: data as ILeagueRank[] });
      log('League', `Loaded leaderboard ranks: `, data);
      return;
    }

    dispatch({
      type: ELeagueActions.LoadRankingsFail,
      logMessage: { message: "Cannot load config", color: error("main") }
    });

    warn('League', "Something went wrong while getting rankings:", res);
  }

  const loadLeaderboardConfig = async () => {
    dispatch({ type: ELeagueActions.LoadConfig });

    let res = await clientLeagueAPI.getConfig(seasonId);

    if (res.success) {
      const config = res.config;
      log('League', `Loaded leaderboard config: `, res.config);
      dispatch({ type: ELeagueActions.LoadConfigSuccess, config });
      return;
    }

    dispatch({
      type: ELeagueActions.LoadConfigFail,
      logMessage: { message: "Cannot load config", color: error("main") }
    });

    warn('League', "Something went wrong while getting configuration:", res);
  }

  const handleReloadRanks = async () => {
    await loadRankings();
  }

  const handleReload = (ev: React.MouseEvent<HTMLButtonElement>) => {
    router.reload();
  }

  const handleGoToGallery = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (session) {
      router.push(`/${EPage.MY_GALLERY}`);
    } else {
      router.push(`/${EPage.RANKING}`);
    }
  }

  const hasNftPrizes = state.activeWeek && state.activeWeek.nftPrizes && state.activeWeek.nftPrizes.length;

  return (
    <Stack className={className} spacing={8} alignItems={"center"}>
      {state.config && <>
          <LeagueHeader config={state.activeWeek} ranks={state.ranks} phase={state.phase}/>
        {hasNftPrizes && <League_Prizes config={state.activeWeek}/>}
          <LeagueLeaderboard ranks={state.ranks} reloadRanks={handleReloadRanks}/>
      </>}

      <EDPageLoader in={state.loading} loadingText={"Loading league"}/>

      {!state.loading && !state.config &&
          <EDOperationPopup in={true} header={"Error"} sx={{ margin: "0 !important" }}>
              <Stack spacing={2} alignItems={"center"} justifyContent={"center"}>
                  <EDTypography variant={"body1"}>
                      Something went wrong while loading.
                  </EDTypography>
                  <LogMessage logMessage={state.logMessage}/>
                  <Stack direction={"row"} spacing={2} alignItems={"center"} justifyContent={"center"}>
                      <EDButton label={"Retry"} onClick={handleReload}/>
                      <EDButton type={EDButtonType.Secondary} label={session ? "Go to gallery" : "View Dragons"}
                                onClick={handleGoToGallery}/>
                  </Stack>
              </Stack>
          </EDOperationPopup>
      }

      <League_DebugTools state={state} dispatch={dispatch}/>
    </Stack>
  );
}

const LeagueStyled = styled(League)(() => ({
  width: "100%"
}));

// Export
export default LeagueStyled;